import { useState, useRef } from 'react';
export function createPcm16RecorderController(config) {
    // Helper: Convert an ArrayBuffer to base64 string.
    const bufferToBase64 = (buffer) => {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    };
    // Helper: Concatenate an array of Uint8Arrays.
    const concatUint8Arrays = (arrays) => {
        const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
        const result = new Uint8Array(totalLength);
        let offset = 0;
        for (const arr of arrays) {
            result.set(arr, offset);
            offset += arr.length;
        }
        return result;
    };
    return {
        useProps(props) {
            const audioCtxRef = useRef(null);
            const workletNodeRef = useRef(null);
            const micStreamRef = useRef(null);
            const [recording, setRecording] = useState(false);
            // Timer state in milliseconds.
            const [timer, setTimer] = useState(0);
            const timerIntervalRef = useRef(null);
            // Ref to hold latest timer value.
            const timerRef = useRef(0);
            // Ref to accumulate all raw PCM chunks (Uint8Array).
            const recordedChunksRef = useRef([]);
            async function start() {
                if (recording) {
                    console.warn('[PCM16Worklet] Already recording.');
                    return;
                }
                setRecording(true);
                // Reset timer and accumulator.
                setTimer(0);
                timerRef.current = 0;
                recordedChunksRef.current = [];
                // Start timer interval: update every 100 ms.
                timerIntervalRef.current = window.setInterval(() => {
                    setTimer((prev) => {
                        const newVal = prev + 100;
                        timerRef.current = newVal;
                        return newVal;
                    });
                }, 100);
                try {
                    // Create AudioContext with sampleRate of 24000 if desired.
                    const audioCtx = new AudioContext({ sampleRate: 24000 });
                    audioCtxRef.current = audioCtx;
                    // Load the AudioWorklet module.
                    await audioCtx.audioWorklet.addModule(config.workerPath);
                    // Create the node.
                    const node = new AudioWorkletNode(audioCtx, 'pcm-processor');
                    workletNodeRef.current = node;
                    // Listen for data from the processor.
                    node.port.onmessage = (event) => {
                        const arrayBuffer = event.data;
                        // Save the raw chunk for later concatenation.
                        recordedChunksRef.current.push(new Uint8Array(arrayBuffer));
                        // Also call onChunk callback.
                        const base64String = bufferToBase64(arrayBuffer);
                        props.onChunk?.({
                            base64: base64String,
                            elapsedMs: timerRef.current,
                        });
                    };
                    // Get microphone audio.
                    const stream = await navigator.mediaDevices.getUserMedia({
                        audio: true,
                    });
                    micStreamRef.current = stream;
                    // Create a source and connect.
                    const source = audioCtx.createMediaStreamSource(stream);
                    source.connect(node);
                    node.connect(audioCtx.destination);
                    props.onStart?.();
                }
                catch (err) {
                    console.error('[PCM16Worklet] Error starting:', err);
                    props.onError?.(err);
                    setRecording(false);
                    if (timerIntervalRef.current !== null) {
                        clearInterval(timerIntervalRef.current);
                        timerIntervalRef.current = null;
                    }
                }
            }
            function stop() {
                if (!recording) {
                    console.warn('[PCM16Worklet] Not recording right now.');
                    return;
                }
                setRecording(false);
                if (timerIntervalRef.current !== null) {
                    clearInterval(timerIntervalRef.current);
                    timerIntervalRef.current = null;
                }
                const finalTimer = timerRef.current;
                setTimer(0);
                timerRef.current = 0;
                // Stop the microphone.
                if (micStreamRef.current) {
                    micStreamRef.current.getTracks().forEach((t) => t.stop());
                    micStreamRef.current = null;
                }
                // Disconnect the node.
                if (workletNodeRef.current) {
                    workletNodeRef.current.port.postMessage('STOP');
                    workletNodeRef.current.disconnect();
                    workletNodeRef.current = null;
                }
                // Close the AudioContext.
                if (audioCtxRef.current) {
                    audioCtxRef.current.close();
                    audioCtxRef.current = null;
                }
                // Concatenate all recorded chunks into one Uint8Array.
                const fullAudioArray = concatUint8Arrays(recordedChunksRef.current);
                // Convert to base64.
                const fullAudioBase64 = bufferToBase64(fullAudioArray.buffer);
                props.onStop?.({ elapsedMs: finalTimer, fullAudio: fullAudioBase64 });
            }
            return {
                state: recording ? 'recording' : 'idle',
                start,
                stop,
                timerMs: timer,
            };
        },
    };
}
//# sourceMappingURL=voiceController.js.map